import api from '@/api/request'
import { flux } from '@/util/func'
import useStore from '@/store/pinia'

// ========================================================
// 一、 密码管理接口 (PasswordManageResource)
// ========================================================

/**
 * 1.1 未登录重置密码 (忘记密码/找回密码场景)
 * @param {Object} data - 请求体参数
 * @param {string} data.phone - 手机号 (巴基斯坦格式，如 3xxxxxxxxx)
 * @param {string} data.code - 短信验证码
 * @param {string} data.newPassword - 新密码（明文，方法内会自动映射为后端下划线键名 new_password）
 * @param {string} [data.clientIp] - 客户端 IP (可选)
 * @param {string} [data.userAgent] - 浏览器 UserAgent (可选)
 * @returns {Promise<{ success: boolean, errorCode: string|null, errorMessage: string|null }>} ForgetPasswordResult
 */
export function retrievePassword(data) {
  // 严格对齐文档：将前端驼峰字段映射为后端指定的下划线 JSON 键名
  const payload = {
    phone: data.phone,
    code: data.code,
    newPassword: data.newPassword,
    clientIp: data.clientIp,
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
  }
  return api.post(`services/uaa/api/retrieve/password`, payload)
}

/**
 * 1.2 已登录状态下重置密码 (最终提交修改)
 * @description 认证要求：需携带 Authorization: Bearer <token> 凭证
 * @param {Object} data - 请求体参数
 * @param {string} data.code - 核心安全凭证：通过 1.4 接口 (verifyLoginStatusCode) 校验验证码成功后，后端响应返回的临时随机码
 * @param {string} data.newPassword - 新密码
 * @returns {Promise<{ success: boolean, errorCode: string|null, errorMessage: string|null }>} ResetPasswordResult
 */
export function resetPassword(data) {
  return api.post(`services/uaa/api/reset/password`, data)
}

/**
 * 1.3 发送已登录状态下的重置密码验证码
 * @description 认证要求：需携带 Authorization: Bearer <token> 凭证（由全局拦截器自动注入）
 * @returns {Promise<{ success: boolean, errorCode: string|null, errorMessage: string|null }>} PasswordSendSmsResult
 */
export function sendPasswordSms() {
  return api.post(`services/uaa/api/password/send/sms`)
}

/**
 * 修改密码 (旧版通用基础密码修改接口)
 * @param {Object} data
 */
export function modifyPassword(data) {
  return api.put(`services/uaa/api/client/modify/password`, data)
}

/**
 * 首次设置密码
 * @param {Object} data
 */
export function setPassword(data) {
  return api.put(`services/user/api/user/set/password`, data)
}

/**
 * 检查当前登录用户是否变更/完成过密码设置
 * @param {Object} data
 */
export function isPassWordChanged(data) {
  return api.get(`services/uaa/api/find/changed/complete`, data)
}

// ========================================================
// 二、 核心登录、注册与验证码模块
// ========================================================

/**
 * 发送短信验证码 (公共多场景业务通用)
 * @param {Object} data - 请求体参数
 * @param {string} data.phone - 手机号码
 * @param {'registration' | 'login' | 'getback'} data.scenario - 验证码业务场景区分
 * @param {string} [data.captchaToken] - 图形验证码 token (频繁发送被安全风控拦截时必填)
 * @param {string} [data.captchaAnswer] - 用户输入的图形验证码答案 (频繁发送被安全风控拦截时必填)
 * @returns {Promise<{ success: boolean, errorCode: string|null, errorMessage: string|null }>}
 */
// 🎯 在函数外层定义一个私有锁，利用闭包，这个锁只有 getCode 能访问到
let isGetCodeLoading = false
export function getCode(data) {
  // return api.post(`services/uaa/api/client/send-sms`, data)
  if (isGetCodeLoading) {
    // 返回一个永远不会 resolve/reject 的 Promise，可以让外层静默，什么都不触发
    return new Promise(() => { })
  }
  // 2. 【上锁】
  isGetCodeLoading = true
  return api.post(`services/uaa/api/client/send-sms`, data).then(res => {
    isGetCodeLoading = false // 成功回包，解锁
    return res
  }).catch(err => {
    isGetCodeLoading = false // 接口报错（网络错/后端错），解锁
    return Promise.reject(err)
  })
}

/**
 * 短信验证码快速登录
 * @param {Object} data
 */
export function login(data) {
  // 清除token
  localStorage.removeItem(`${process.env.NODE_ENV}_token`)
  return api
    .post('services/uaa/api/client/login/sms', data)
    .then((res) => {
      console.log('login res', res?.accessToken)
      if (res && res.accessToken) {
        const { userStore } = useStore()
        if (typeof userStore.setToken === 'function') {
          userStore.setToken(res.accessToken)
        }
        localStorage.setItem(`${process.env.NODE_ENV}_token`, res.accessToken)
      }
      return flux.resolve(res)
    })
    .catch((err) => {
      return flux.reject(err)
    })
}

/**
 * 账号密码常规登录（兼容旧系统 PRICOO 逻辑）
 * @param {Object} data
 */
export function passwordLogin(data) {
  // 清除token
  localStorage.removeItem(`${process.env.NODE_ENV}_token`)
  const apiUrl = 'services/uaa/api/client/login/password'

  return api
    .post(apiUrl, data)
    .then((res) => {
      console.log('login res', res?.accessToken)
      if (res && res.accessToken) {
        const { userStore } = useStore()
        if (typeof userStore.setToken === 'function') {
          userStore.setToken(res.accessToken)
        }
        localStorage.setItem(`${process.env.NODE_ENV}_token`, res.accessToken)
      }
      return flux.resolve(res)
    })
    .catch((err) => {
      return flux.reject(err)
    })
}

/**
 * 获取防刷图形验证码
 * @returns {Promise<{ success: boolean, captchaToken: string, captchaImage: string, errorCode: string|null, errorMessage: string|null }>}
 */
export function getCaptcha() {
  return api.get(`services/uaa/api/client/captcha`)
}

/**
 * 短信验证码注册 (新版用户创建)
 * @param {Object} data - 请求体
 * @param {string} data.phone - 手机号码
 * @param {string} data.password - 密码
 * @param {string} data.verificationCode - 短信验证码
 */
export function registerBySms(data) {
  // 清除token
  localStorage.removeItem(`${process.env.NODE_ENV}_token`)

  return api
    .post(`services/uaa/api/client/register/sms`, data)
    .then((res) => {
      if (res && res.success && res.accessToken) {
        const { userStore } = useStore()
        if (typeof userStore.setToken === 'function') {
          userStore.setToken(res.accessToken)
        }
        localStorage.setItem(`${process.env.NODE_ENV}_token`, res.accessToken)
      }
      return flux.resolve(res)
    })
    .catch((err) => {
      return flux.reject(err)
    })
}

// ========================================================
// 三、 Token 运维管理模块
// ========================================================

/**
 * 刷新 Token (使用过期前的持久刷新令牌换取新访问令牌)
 * @param {Object} data - 请求参数
 * @param {string} data.refreshToken - 刷新令牌字符串
 * @returns {Promise<{ success: boolean, accessToken: string, tokenType: string, expiresIn: number, refreshToken: string, errorCode: string|null, errorMessage: string|null }>}
 */
export function refreshToken(data) {
  return api.post(`services/uaa/api/client/refresh-token`, data)
}

/**
 * 安全退出登录 (吊销当前服务端 Token 状态)
 * @description 依赖请求头中的 Authorization Bearer 完成鉴权与服务端销毁
 * @returns {Promise<{ success: boolean, errorCode: string|null, errorMessage: string|null }>}
 */
export function signOut() {
  return api
    .post(`services/uaa/api/client/signout`)
    .then((res) => {
      if (res && res.success) {
        const { userStore } = useStore()
        localStorage.removeItem(`${process.env.NODE_ENV}_token`)
        if (typeof userStore.setToken === 'function') {
          userStore.setToken('')
        }
        if (typeof userStore.setUserInfo === 'function') {
          userStore.setUserInfo(null)
        }
      }
      return flux.resolve(res)
    })
    .catch((err) => {
      return flux.reject(err)
    })
}